import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useDidUpdateEffect } from "../hooks";
import { BrowserStorageIdsEnum } from "./browser.storage.provider";

const convertToLocalStorageItems = (games: IMultiviewGame[]) => {
    return games.map((game) => ({
        ...game,
    }));
};

export type IMultiviewGame = {
    gameId: number;
    sportId: number;
    categoryId: number;
    tournamentId: number;
};

interface IMultiViewContext {
    games: IMultiviewGame[];
    addGame: (game: IMultiviewGame) => void;
    removeGame: (id: number) => void;
    removeAllGames: () => void;
    reorderGames: (dragIndex: number, hoverIndex: number) => void;
    isSportSelected: (id: number) => boolean;
    isCategorySelected: (id: number) => boolean;
    isTournamentSelected: (id: number) => boolean;
    isGameSelected: (gameId: number) => boolean;
}

export const MultiViewContext = createContext<IMultiViewContext>(null as any);

export const MultiViewProvider: FC<PropsWithChildren> = ({ children }) => {
    const [games, setGames] = useState<IMultiviewGame[]>([]);

    useEffect(() => {
        try {
            const storageTournaments =
                sessionStorage.getItem(BrowserStorageIdsEnum.LIVE_MULTI_VIEW) ?? "[]";
            const parsedArr: IMultiviewGame[] = JSON.parse(storageTournaments);

            if (parsedArr.length) {
                setGames(parsedArr);
            }
        } catch {
            // malformed sessionStorage content — start with empty list
        }
    }, []);

    useDidUpdateEffect(() => {
        sessionStorage.setItem(
            BrowserStorageIdsEnum.LIVE_MULTI_VIEW,
            JSON.stringify(convertToLocalStorageItems(games)),
        );
    }, [games]);

    const addGame = useCallback(({ gameId, sportId, categoryId, tournamentId }: IMultiviewGame) => {
        setGames((state) => [...state, { gameId, sportId, categoryId, tournamentId }]);
    }, []);

    const removeGame = useCallback(
        (id: number) => {
            setGames(games.filter((game) => game.gameId !== id));
        },
        [games],
    );

    const reorderGames = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            const result = [...games];

            result.splice(dragIndex, 1);
            result.splice(hoverIndex, 0, games[dragIndex]);

            setGames(result);
        },
        [games],
    );

    const removeAllGames = useCallback(() => setGames([]), []);

    const sportIdsSet = useMemo(() => new Set(games.map((g) => g.sportId)), [games]);
    const categoryIdsSet = useMemo(() => new Set(games.map((g) => g.categoryId)), [games]);
    const tournamentIdsSet = useMemo(() => new Set(games.map((g) => g.tournamentId)), [games]);
    const gameIdsSet = useMemo(() => new Set(games.map((g) => g.gameId)), [games]);

    const isSportSelected = useCallback((id: number) => sportIdsSet.has(id), [sportIdsSet]);

    const isCategorySelected = useCallback(
        (id: number) => categoryIdsSet.has(id),
        [categoryIdsSet],
    );

    const isTournamentSelected = useCallback(
        (id: number) => tournamentIdsSet.has(id),
        [tournamentIdsSet],
    );

    const isGameSelected = useCallback((id: number) => gameIdsSet.has(id), [gameIdsSet]);

    const value = useMemo(
        () => ({
            games,
            addGame,
            removeGame,
            removeAllGames,
            reorderGames,
            isSportSelected,
            isCategorySelected,
            isTournamentSelected,
            isGameSelected,
        }),
        [
            games,
            addGame,
            removeGame,
            removeAllGames,
            reorderGames,
            isSportSelected,
            isCategorySelected,
            isTournamentSelected,
            isGameSelected,
        ],
    );

    return <MultiViewContext.Provider value={value}>{children}</MultiViewContext.Provider>;
};
